import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import {
  Autocomplete,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";

import {
  addSharePerson,
  getShare,
  lookupSharePerson,
  removeSharePerson,
  setShareMode,
  shareLinkForConversation,
  type ShareAccessMode,
  type ShareState,
} from "../requests";
import { useSnackbarStore } from "common/services/snackbar";
import { useLanguage } from "contexts/LanguageContext";

import * as S from "./style";
import type { PersonOption, ShareConversationDialogProps } from "./types";

const emailOf = (option: PersonOption | string): string =>
  typeof option === "string" ? option : option.email;

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

function personAvatar(fullName: string | null, avatarUrl: string | null, size: number) {
  return (
    <Avatar src={avatarUrl ?? undefined} sx={{ width: size, height: size }}>
      {fullName ? fullName[0]?.toUpperCase() : undefined}
    </Avatar>
  );
}

type ShareBodyProps = {
  composing: boolean;
  people: ShareState["people"];
  mode: ShareAccessMode;
  onRemove: (grantId: string) => void;
  onMode: (mode: ShareAccessMode) => void;
  t: (key: string, options?: any) => string;
};

// Everything below the "Add people" field. Memoised and given only props that do
// not change while typing, so a keystroke in the Autocomplete re-renders the
// field alone — not the people-list, avatars and access panels underneath it.
// That per-keystroke reconciliation was the source of the typing lag.
const ShareBody = memo(function ShareBody(props: ShareBodyProps) {
  if (props.composing) {
    // While people are staged the footer becomes "Send"; there is no email
    // notification infrastructure yet, so no notify/note controls are shown.
    return null;
  }

  return (
    <>
      <S.SectionTitle>{props.t('share.peopleWithAccess')}</S.SectionTitle>
      <S.PeopleList>
        {props.people.map((person) => (
          <S.PersonRow key={person.email}>
            {personAvatar(person.full_name, person.avatar_url, 32)}
            <S.PersonMeta>
              <S.Name variant="body2">{person.full_name ?? person.email}</S.Name>
              <S.Email>{person.email}</S.Email>
            </S.PersonMeta>
            {person.removable && person.grant_id ? (
              <IconButton
                size="small"
                aria-label={props.t('share.removePerson', { email: person.email })}
                onClick={() => props.onRemove(person.grant_id as string)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : (
              <S.AccessLevel>{person.access_level}</S.AccessLevel>
            )}
          </S.PersonRow>
        ))}
      </S.PeopleList>

      <S.SectionTitle>{props.t('share.generalAccess')}</S.SectionTitle>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {props.mode === "anyone" ? (
          <S.PublicAccessIcon color="action" />
        ) : (
          <S.RestrictedAccessIcon color="action" />
        )}
        <FormControl size="small" sx={{ minWidth: 220, border: "none" }}>
          <Select
            value={props.mode}
            onChange={(e) => props.onMode(e.target.value as ShareAccessMode)}
            sx={{
              boxShadow: "none",
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
            }}
          >
            <MenuItem value="restricted">{props.t('share.restricted')}</MenuItem>
            <MenuItem value="anyone">{props.t('share.anyoneWithLink')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {props.mode === "anyone"
          ? props.t('share.anyoneCanView')
          : props.t('share.onlyAddedCanView')}
      </Typography>
    </>
  );
});

export function ShareConversationDialog(props: ShareConversationDialogProps) {
  const notify = useSnackbarStore((s) => s.notify);
  const { t } = useLanguage();
  const [share, setShare] = useState<ShareState | null>(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<PersonOption[]>([]);
  // People chosen in the "Add people" box but not yet committed. While this is
  // non-empty the dialog is "composing": the footer becomes Send and the access
  // panels are hidden.
  const [pending, setPending] = useState<PersonOption[]>([]);
  const [sending, setSending] = useState(false);

  // Fresh state every time the dialog opens — the link, mode and people-list may
  // have changed since it was last seen. Any half-composed selection is dropped.
  useEffect(() => {
    if (!props.open) return;
    let active = true;
    setLoading(true);
    setPending([]);
    setInputValue("");
    getShare(props.conversationId)
      .then((state) => active && setShare(state))
      .catch((err) => notify(`${t('share.loadError')} ${(err as Error).message}`, "error"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [props.open, props.conversationId, notify]);

  // Server-side exact-email typeahead, debounced. A miss still offers the raw
  // address so an external person with no account yet can be added.
  useEffect(() => {
    const query = inputValue.trim();
    if (!query.includes("@")) {
      // Only touch state when it actually changes — a new `[]` every keystroke
      // would re-render for nothing while typing a name before the address.
      setOptions((prev) => (prev.length ? [] : prev));
      return;
    }
    let stale = false;
    const handle = setTimeout(async () => {
      try {
        const result = await lookupSharePerson(props.conversationId, query);
        if (stale) return; // a newer keystroke superseded this lookup
        setOptions([
          {
            email: result.email,
            full_name: result.full_name,
            avatar_url: result.avatar_url,
            exists: result.exists,
          },
        ]);
      } catch {
        if (!stale) {
          setOptions([{ email: query, full_name: null, avatar_url: null, exists: false }]);
        }
      }
    }, 250);
    return () => {
      stale = true;
      clearTimeout(handle);
    };
  }, [inputValue, props.conversationId]);

  const people = share?.people ?? [];
  const composing = pending.length > 0;

  // Selecting a person only stages them — nothing is shared until Send. Raw
  // addresses are validated here; anyone who already has access is skipped.
  const handleSelect = useCallback(
    (_event: unknown, next: (PersonOption | string)[]) => {
      const access = new Set((share?.people ?? []).map((p) => p.email.toLowerCase()));
      const seen = new Set<string>();
      const staged: PersonOption[] = [];
      for (const item of next) {
        const email = emailOf(item).trim();
        const key = email.toLowerCase();
        if (!key || seen.has(key)) continue;
        if (typeof item === "string" && !isEmail(email)) {
          notify(t('share.invalidEmail'), "warning");
          continue;
        }
        if (access.has(key)) {
          notify(t('share.alreadyHasAccess', { email }), "warning");
          continue;
        }
        seen.add(key);
        if (typeof item === "string") {
          const match = options.find((o) => o.email.toLowerCase() === key);
          staged.push(match ?? { email, full_name: null, avatar_url: null, exists: false });
        } else {
          staged.push(item);
        }
      }
      setPending(staged);
      setInputValue("");
    },
    [share, options, notify, t],
  );

  // Commit every staged person. The endpoint returns the refreshed state each
  // time, so the last response already carries the whole updated people-list.
  const handleSend = useCallback(async () => {
    if (!pending.length) return;
    setSending(true);
    try {
      let latest: ShareState | null = share;
      for (const person of pending) {
        latest = await addSharePerson(props.conversationId, person.email.trim(), false);
      }
      if (latest) setShare(latest);
      const count = pending.length;
      setPending([]);
      setInputValue("");
      notify(count === 1 ? t('share.personAdded') : t('share.peopleAdded', { count }), "success");
    } catch (err) {
      // The share endpoint answers 404 for an unknown email and 409 when the
      // person already has access; surface the translated copy for both.
      const status = (err as any)?.response?.status;
      const message =
        status === 404
          ? t('share.userNotFound')
          : status === 409
            ? t('share.alreadyShared')
            : `${t('share.addError')} ${(err as Error).message}`;
      notify(message, "error");
    } finally {
      setSending(false);
    }
  }, [pending, share, props.conversationId, notify, t]);

  const handleRemove = useCallback(
    async (grantId: string) => {
      try {
        setShare(await removeSharePerson(props.conversationId, grantId));
      } catch (err) {
        notify(`${t('share.updateError')} ${(err as Error).message}`, "error");
      }
    },
    [notify, props.conversationId, t],
  );

  const handleMode = useCallback(
    async (mode: ShareAccessMode) => {
      try {
        setShare(await setShareMode(props.conversationId, mode));
      } catch (err) {
        notify(`${t('share.modeError')} ${(err as Error).message}`, "error");
      }
    },
    [notify, props.conversationId, t],
  );

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLinkForConversation(props.conversationId));
      notify(t('share.linkCopied'), "success");
    } catch {
      notify(t('share.copyError'), "error");
    }
  }, [notify, props.conversationId, t]);

  const mode = share?.access_mode ?? "restricted";

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('share.dialogTitle', { title: props.conversationTitle })}</DialogTitle>
      <DialogContent>
        {loading && !share ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <Autocomplete
              sx={{ mt: 1 }}
              multiple
              freeSolo
              options={options}
              value={pending}
              inputValue={inputValue}
              onInputChange={(_e, value) => setInputValue(value)}
              onChange={handleSelect}
              filterOptions={(x) => x}
              getOptionLabel={(option) => emailOf(option)}
              isOptionEqualToValue={(option, value) =>
                emailOf(option).toLowerCase() === emailOf(value).toLowerCase()
              }
              renderOption={(optionProps, option) => {
                const email = emailOf(option);
                const person = typeof option === "string" ? undefined : option;
                return (
                  <li {...optionProps} key={email}>
                    <S.OptionRow>
                      {personAvatar(person?.full_name ?? null, person?.avatar_url ?? null, 28)}
                      <S.PersonMeta>
                        {person?.full_name ? (
                          <>
                            <S.Name variant="body2">{person.full_name}</S.Name>
                            <S.Email>{email}</S.Email>
                          </>
                        ) : (
                          <S.Name variant="body2">{email}</S.Name>
                        )}
                      </S.PersonMeta>
                    </S.OptionRow>
                  </li>
                );
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const email = emailOf(option);
                  const person = typeof option === "string" ? undefined : option;
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...chipProps}
                      avatar={personAvatar(person?.full_name ?? null, person?.avatar_url ?? null, 24)}
                      label={person?.full_name ?? email}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params} label={t('share.addPeople')} placeholder={t('share.emailPlaceholder')} />
              )}
            />

            <ShareBody
              t={t}
              composing={composing}
              people={people}
              mode={mode}
              onRemove={handleRemove}
              onMode={handleMode}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <S.Footer>
          <Button startIcon={<LinkIcon />} onClick={copyLink} disabled={!share}>
            {t('share.copyLink')}
          </Button>
          <S.Spacer />
          {composing ? (
            <Button variant="contained" onClick={handleSend} disabled={sending}>
              {sending ? t('share.sending') : t('share.send')}
            </Button>
          ) : (
            <Button variant="contained" onClick={props.onClose}>
              {t('share.done')}
            </Button>
          )}
        </S.Footer>
      </DialogActions>
    </Dialog>
  );
}
